import { useState } from "react";
import { Clock, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { AdminImg } from "./AdminImg";

const MIN_REASON = 3;
const NAMES = { front: "Front", back: "Back", left: "Left side", right: "Right side", closeup: "Face close-up" };
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "");

// One held duck: the held images (served from the duck, never copied), why, and the override box (reason required).
export const HeldDuckRow = ({ duck, onOverride }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const views = Object.keys(duck.views).includes("front") ? Object.keys(duck.views) : ["front", ...Object.keys(duck.views)];
  const ok = reason.trim().length >= MIN_REASON;
  const submit = async () => {
    setBusy(true);
    try {
      await onOverride(duck.id, reason.trim());
    } finally {
      setBusy(false);
    }
  };
  return (
    <li data-testid={`held-duck-${duck.id}`} className="rounded-3xl border border-border bg-card p-4 sm:p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="min-w-0">
          <p className="font-heading font-bold text-base md:text-lg truncate">{duck.name || "Unnamed duck"}</p>
          <p className="text-xs text-muted-foreground font-mono2">
            {duck.maker || "unknown maker"} · {duck.hold === "upload" ? "upload held before its paid views" : "Start sculpting held"} · {duck.id.slice(0, 8)}
          </p>
        </div>
        {duck.review ? (
          <span data-testid={`held-waiting-${duck.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs font-semibold">
            <Clock className="h-3.5 w-3.5" /> Waiting for review since {when(duck.review.asked_at)}
          </span>
        ) : (
          <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">Held, no review asked</span>
        )}
      </div>
      <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {views.map((v) => (
          <li key={v} className={`rounded-2xl border-2 overflow-hidden ${duck.views[v] ? "border-destructive/60" : "border-border"}`}>
            <AdminImg testid={`held-img-${duck.id}-${v}`} src={`/held-ducks/${duck.id}/image/${v}.png`} alt={`${NAMES[v] || v} view`} className="w-full aspect-square object-contain bg-muted/40" />
            <p className="px-2 py-1 text-[11px] font-mono2 uppercase tracking-wide text-muted-foreground">{NAMES[v] || v}</p>
          </li>
        ))}
      </ul>
      <ul className="space-y-1 text-sm list-disc pl-5">
        {Object.entries(duck.views).map(([v, b]) => (
          <li key={v} data-testid={`held-reason-${duck.id}-${v}`}><span className="font-semibold">{b.code}</span> · {b.reason}</li>
        ))}
      </ul>
      <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
        <Textarea
          data-testid={`held-reason-input-${duck.id}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={300}
          rows={2}
          placeholder="Why is it fine to sculpt? (required, saved in the audit log)"
          className="rounded-2xl text-sm"
        />
        <Button data-testid={`held-override-btn-${duck.id}`} disabled={!ok || busy} onClick={submit} className="rounded-full h-11 gap-2 font-bold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Let it through
        </Button>
      </div>
    </li>
  );
};
